import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "@/lib/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/coverage", label: "Coverage" },
  { to: "/wavfiber", label: "WAVFiber" },
  { to: "/streaming", label: "Streaming" },
  { to: "/wifi-consulting", label: "WiFi Consulting" },
  { to: "/business", label: "Business" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-300 ${
        scrolled ? "glass-nav border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <img src="/brand/logo-white.png" alt="CYAN Wireless" className="h-7 sm:h-8 w-auto" />
        </Link>

        <ul className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-cyan-400" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-4">
          <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-cyan-400 transition-colors" data-testid="nav-phone">
            <Phone size={15} /> {SITE.phone}
          </a>
          <Link
            to="/survey"
            className="bg-cyan-500 text-ink-950 font-display font-bold text-sm px-5 py-2.5 rounded-full btn-glow hover:bg-cyan-400"
            data-testid="nav-start-here"
          >
            Start Here
          </Link>
        </div>

        <button
          className="lg:hidden text-white p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass-nav border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-4 flex flex-col">
              {LINKS.map((l) => (
                <li key={l.to} className="border-b border-white/5">
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `block py-4 font-display font-bold text-lg ${isActive ? "text-cyan-400" : "text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-5 flex flex-col gap-3">
                <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2 text-zinc-300">
                  <Phone size={16} /> {SITE.phone}
                </a>
                <Link to="/survey" className="bg-cyan-500 text-ink-950 text-center font-display font-bold px-5 py-3 rounded-full">
                  Start Here
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
